import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../cart";
import { track, trackFunnel } from "../analytics";
import ShareButton from "./ShareButton";
import ResponsiveImage from "./ResponsiveImage";
import ProductReviews, { ReviewSummary } from "./ProductReviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============ Product JSON-LD (rich SERP results: price, stars, availability) ============ */
function ProductSchema({ products, reviewsSummary }) {
    useEffect(() => {
        const SCRIPT_ID = "fgm-products-jsonld";
        document.getElementById(SCRIPT_ID)?.remove();
        if (!products || products.length === 0) return;
        const items = products.map((p) => {
            const summary = reviewsSummary?.[p.id];
            const imgs = (p.images || []).map((im) =>
                typeof im === "string" ? im : (im?.url || "")
            ).filter(Boolean);
            const offer = {
                "@type": "Offer",
                "url": `https://fgmapparel.com/#shop`,
                "priceCurrency": "USD",
                "price": p.price?.toFixed(2),
                "availability": p.sold_out
                    ? "https://schema.org/SoldOut"
                    : "https://schema.org/InStock",
                "itemCondition": "https://schema.org/NewCondition",
                "seller": { "@type": "Organization", "name": "FGM Apparel" },
            };
            const node = {
                "@context": "https://schema.org",
                "@type": "Product",
                "name": p.name,
                "sku": p.id,
                "description": p.description || p.tagline || `${p.name} — FGM Apparel premium streetwear.`,
                "image": imgs.length ? imgs : ["https://fgmapparel.com/brand/White.png"],
                "brand": { "@type": "Brand", "name": "FGM Apparel" },
                "category": p.category || "Apparel",
                "offers": offer,
            };
            if (summary && summary.count > 0 && summary.avg > 0) {
                node.aggregateRating = {
                    "@type": "AggregateRating",
                    "ratingValue": Number(summary.avg).toFixed(1),
                    "reviewCount": summary.count,
                    "bestRating": "5",
                    "worstRating": "1",
                };
            }
            return node;
        });
        const el = document.createElement("script");
        el.id = SCRIPT_ID;
        el.type = "application/ld+json";
        el.text = JSON.stringify(items);
        document.head.appendChild(el);
        return () => document.getElementById(SCRIPT_ID)?.remove();
    }, [products, reviewsSummary]);
    return null;
}

// Summer Heat Drop ships June 19, 2026 — until then, available items are in PRE-ORDER mode.
const DROP_DATE = new Date("2026-06-19T00:00:00-04:00");

function useCountdown(target) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const diff = Math.max(0, target.getTime() - now.getTime());
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { days, hours, minutes, seconds, isLive: diff === 0 };
}

function DropCountdown() {
    const { days, hours, minutes, seconds, isLive } = useCountdown(DROP_DATE);
    if (isLive) return null;
    const cells = [
        { label: "Days", value: days },
        { label: "Hours", value: hours },
        { label: "Mins", value: minutes },
        { label: "Secs", value: seconds },
    ];
    return (
        <div
            data-testid="drop-countdown"
            className="mb-12 border-y border-white/10 py-6 grid grid-cols-2 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-center"
        >
            <div className="col-span-2 md:col-span-1 flex items-center gap-3">
                <span className="h-2 w-2 bg-[#E63946] animate-pulse" />
                <span className="font-display tracking-[0.3em] text-xs text-zinc-300 uppercase whitespace-nowrap">
                    Summer Heat Drop · Pre-Order · Ships Jun 19
                </span>
            </div>
            <div className="col-span-2 md:col-span-1 grid grid-cols-4 gap-2 md:gap-4 md:justify-self-end">
                {cells.map((c) => (
                    <div
                        key={c.label}
                        data-testid={`countdown-${c.label.toLowerCase()}`}
                        className="text-center"
                    >
                        <div className="font-display text-3xl md:text-4xl text-white tabular-nums leading-none">
                            {String(c.value).padStart(2, "0")}
                        </div>
                        <div className="font-display text-[10px] tracking-[0.3em] text-zinc-500 uppercase mt-2">
                            {c.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function isPreorderWindow() {
    return Date.now() < DROP_DATE.getTime();
}

function StatusPill({ status, preorder }) {
    if (status === "available") {
        if (preorder) {
            return (
                <span className="text-[10px] font-display tracking-[0.3em] text-[#E63946] uppercase">
                    ● Pre-Order · Ships Jun 19
                </span>
            );
        }
        return (
            <span className="text-[10px] font-display tracking-[0.3em] text-[#E63946] uppercase">
                ● In Stock
            </span>
        );
    }
    if (status === "coming_soon") {
        return (
            <span className="text-[10px] font-display tracking-[0.3em] text-[#1D4ED8] uppercase">
                ● Coming Soon
            </span>
        );
    }
    return (
        <span className="text-[10px] font-display tracking-[0.3em] text-zinc-500 uppercase">
            ● Sold Out
        </span>
    );
}

function ProductCard({ product, onNotify, reviewSummary }) {
    const { addItem } = useCart();
    const [size, setSize] = useState(product.sizes[0]);
    const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
    const [color, setColor] = useState(hasColors ? product.colors[0] : null);
    const galleryImages = product.images && product.images.length > 0 ? product.images : [product.image];
    const [imgIdx, setImgIdx] = useState(0);

    // When user picks a color and the product has matched images per colorway,
    // jump the gallery to the matching image (assumes images[i] aligns with colors[i]).
    const onColorPick = (c) => {
        setColor(c);
        if (hasColors) {
            const ci = product.colors.indexOf(c);
            if (ci >= 0 && ci < galleryImages.length) setImgIdx(ci);
        }
    };

    const isAvailable = product.status === "available";
    const isComingSoon = product.status === "coming_soon";
    const isPreorder = isAvailable && isPreorderWindow();
    const isFeatured = !!product.featured;

    return (
        <div
            data-testid={`product-card-${product.id}`}
            className={`group bg-fgm-surface border transition-colors flex flex-col relative ${
                isFeatured
                    ? "fgm-featured-card md:col-span-2 lg:col-span-2 border-[#E63946]/60 hover:border-[#E63946]"
                    : "border-white/10 hover:border-white/30"
            }`}
        >
            {isFeatured && (
                <>
                    {/* Ribbon top-left */}
                    <div
                        data-testid={`product-featured-ribbon-${product.id}`}
                        className="absolute top-0 left-0 z-20 bg-gradient-to-r from-[#E63946] to-[#ff7847] text-white font-display tracking-[0.3em] text-[10px] md:text-[11px] uppercase px-4 py-1.5 shadow-lg"
                    >
                        1 of 1 · Never Replicated
                    </div>
                    {/* Subtle outer ring glow */}
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -inset-px bg-gradient-to-br from-[#E63946]/40 via-transparent to-[#ff7847]/30 blur-md opacity-60 -z-10"
                    />
                </>
            )}
            <div className="relative aspect-[4/5] overflow-hidden bg-white">
                {isAvailable ? (
                    <>
                        <ResponsiveImage
                            key={imgIdx}
                            src={galleryImages[imgIdx]}
                            alt={product.name}
                            sizes="(max-width: 480px) 400px, (max-width: 1024px) 400px, 400px"
                            className="w-full h-full object-contain transition-opacity duration-300"
                        />
                        {galleryImages.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    data-testid={`product-img-prev-${product.id}`}
                                    onClick={() => setImgIdx((i) => (i - 1 + galleryImages.length) % galleryImages.length)}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black text-white w-8 h-8 flex items-center justify-center"
                                    aria-label="Previous image"
                                >
                                    ‹
                                </button>
                                <button
                                    type="button"
                                    data-testid={`product-img-next-${product.id}`}
                                    onClick={() => setImgIdx((i) => (i + 1) % galleryImages.length)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black text-white w-8 h-8 flex items-center justify-center"
                                    aria-label="Next image"
                                >
                                    ›
                                </button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                                    {galleryImages.map((_, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            data-testid={`product-img-dot-${product.id}-${i}`}
                                            onClick={() => setImgIdx(i)}
                                            className={`w-2 h-2 transition-colors ${i === imgIdx ? "bg-[#E63946]" : "bg-black/40 hover:bg-black/70"}`}
                                            aria-label={`Image ${i + 1}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <div
                        data-testid={`product-placeholder-${product.id}`}
                        className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#121212] via-black to-[#1a1a1a]"
                    >
                        <img
                            src="/brand/White.png"
                            alt=""
                            aria-hidden
                            className="w-3/5 max-w-[220px] opacity-30 transition-transform duration-700 group-hover:scale-105"
                         loading="lazy" decoding="async" />
                        <div className="absolute bottom-6 left-0 right-0 text-center">
                            <span className="text-[10px] font-display tracking-[0.35em] text-zinc-600 uppercase">
                                Summer Heat Drop · {product.category}
                            </span>
                        </div>
                    </div>
                )}
                {!isAvailable && (
                    <div className="absolute top-3 left-3 bg-black/80 border border-white/20 px-3 py-1.5 backdrop-blur-md">
                        <span className="font-display text-xs tracking-[0.25em] text-white uppercase">
                            {isComingSoon ? "Coming Soon" : "Sold Out"}
                        </span>
                    </div>
                )}
                {isAvailable && (
                    <div className="absolute bottom-3 right-3 bg-black/70 px-2.5 py-1 backdrop-blur-md">
                        <span className="font-display text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
                            {product.category}
                        </span>
                    </div>
                )}
                {isPreorder && (
                    <div
                        data-testid={`product-preorder-badge-${product.id}`}
                        className="absolute top-3 left-3 bg-[#E63946] px-3 py-1.5"
                    >
                        <span className="font-display text-[10px] tracking-[0.3em] text-white uppercase">
                            Drops Jun 19
                        </span>
                    </div>
                )}
                <ShareButton product={product} />
            </div>

            <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-2 gap-3">
                    <h3 className="font-display text-xl text-white uppercase leading-tight">
                        {product.name}
                    </h3>
                    <div className="font-display text-2xl text-white whitespace-nowrap">
                        ${Number.isInteger(product.price) ? product.price : product.price.toFixed(2)}
                    </div>
                </div>
                <p className="text-sm text-zinc-400 mb-4 flex-1 leading-relaxed">
                    {product.description}
                </p>

                {reviewSummary && reviewSummary.count > 0 && (
                    <div className="mb-3">
                        <ReviewSummary summary={reviewSummary} />
                    </div>
                )}

                <div className="mb-4">
                    <StatusPill status={product.status} preorder={isPreorder} />
                </div>

                {isAvailable ? (
                    <div className="space-y-3">
                        {hasColors && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="fgm-label !mb-0 !text-[10px] mr-1">Color</span>
                                {product.colors.map((c) => (
                                    <button
                                        key={c}
                                        data-testid={`product-color-${product.id}-${c.toLowerCase()}`}
                                        onClick={() => onColorPick(c)}
                                        className={`px-3 py-1.5 text-xs font-display tracking-widest border transition-colors ${
                                            color === c
                                                ? "bg-[#E63946] text-white border-[#E63946]"
                                                : "border-white/20 text-zinc-400 hover:border-white/50 hover:text-white"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="fgm-label !mb-0 !text-[10px] mr-1">Size</span>
                            {product.sizes.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`product-size-${product.id}-${s}`}
                                    onClick={() => setSize(s)}
                                    className={`px-3 py-1.5 text-xs font-display tracking-widest border transition-colors ${
                                        size === s
                                            ? "bg-white text-black border-white"
                                            : "border-white/20 text-zinc-400 hover:border-white/50 hover:text-white"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid={`product-add-${product.id}`}
                            onClick={() => {
                                addItem(product, size, color);
                                const variant = color ? ` (${color})` : "";
                                track("add_to_cart", {
                                    currency: "USD",
                                    value: product.price,
                                    items: [{
                                        item_id: product.id,
                                        item_name: product.name,
                                        item_category: product.category,
                                        item_variant: [color, size].filter(Boolean).join(" / "),
                                        price: product.price,
                                        quantity: 1,
                                    }],
                                });
                                trackFunnel("add_to_cart");
                                toast.success(
                                    isPreorder
                                        ? `${product.name}${variant} reserved · Ships Jun 19`
                                        : `${product.name}${variant} added to cart`
                                );
                            }}
                            className="btn-primary w-full !py-3 !text-base"
                        >
                            {isPreorder ? "Pre-Order Now →" : "Add To Cart →"}
                        </button>
                    </div>
                ) : (
                    <button
                        data-testid={`product-notify-${product.id}`}
                        onClick={() => onNotify(product)}
                        className="btn-outline w-full !py-3 !text-base"
                        disabled={!isComingSoon}
                    >
                        {isComingSoon ? "Notify Me When It Drops" : "Sold Out"}
                    </button>
                )}

                <ProductReviews productId={product.id} />
            </div>
        </div>
    );
}

function NotifyModal({ product, onClose }) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    if (!product) return null;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/drop-notify`, {
                email,
                product_id: product.id,
            });
            toast.success("You're on the list. We'll alert you on drop day.");
            onClose();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) {
                toast.info("You're already on the notify list for this drop.");
                onClose();
            } else {
                toast.error("Couldn't add you. Try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="notify-modal"
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 reveal"
            onClick={onClose}
        >
            <div
                className="bg-fgm-surface border border-white/10 max-w-md w-full p-8 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    data-testid="notify-modal-close"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-zinc-400 hover:text-white text-2xl leading-none"
                    aria-label="Close"
                >
                    ×
                </button>
                <div className="fgm-stripe-divider w-20 mb-5" />
                <h3 className="font-display text-3xl text-white uppercase mb-2">
                    Notify Me
                </h3>
                <p className="text-zinc-400 mb-2">
                    Be first to know when{" "}
                    <span className="text-white">{product.name}</span> drops.
                </p>
                <p className="text-xs font-display tracking-[0.3em] text-[#E63946] uppercase mb-6">
                    Drop-day priority access
                </p>
                <form onSubmit={submit} data-testid="notify-form" className="space-y-4">
                    <div>
                        <label className="fgm-label">Email</label>
                        <input
                            data-testid="notify-input-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="fgm-input"
                            placeholder="your@email.com"
                        />
                    </div>
                    <button
                        data-testid="notify-submit"
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full disabled:opacity-50"
                    >
                        {loading ? "Adding…" : "Lock My Spot"}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default function Shop() {
    const [products, setProducts] = useState([]);
    const [reviewsSummary, setReviewsSummary] = useState({});
    const [notifyFor, setNotifyFor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("ALL");

    useEffect(() => {
        axios
            .get(`${API}/products`)
            .then((r) => setProducts(r.data))
            .catch(() => toast.error("Couldn't load products"))
            .finally(() => setLoading(false));
        axios
            .get(`${API}/reviews-summary`)
            .then((r) => setReviewsSummary(r.data || {}))
            .catch(() => { /* silent */ });
    }, []);

    // ─── D) Category filter order: premium → tops → bottoms → accessories ───
    const CATEGORY_ORDER = [
        "ONE OF ONE",
        "HOODIES",
        "CREWNECKS",
        "T-SHIRTS",
        "TANKS",
        "SHORTS",
        "TRAINING",
        "HEADWEAR",
    ];
    const presentCats = new Set(products.map((p) => p.category));
    const orderedCats = CATEGORY_ORDER.filter((c) => presentCats.has(c));
    // Append any unknown/new categories at the end so nothing disappears if you add a new one
    const extraCats = [...presentCats].filter((c) => !CATEGORY_ORDER.includes(c));
    const categories = ["ALL", ...orderedCats, ...extraCats];

    // ─── C) Default product sort: featured first, then category priority, then price desc ───
    const catIndex = (c) => {
        const i = CATEGORY_ORDER.indexOf(c);
        return i === -1 ? 999 : i;
    };
    const sortedProducts = [...products].sort((a, b) => {
        if (!!b.featured - !!a.featured) return !!b.featured - !!a.featured;
        const d = catIndex(a.category) - catIndex(b.category);
        if (d !== 0) return d;
        return (b.price || 0) - (a.price || 0);
    });

    const visible =
        activeCategory === "ALL"
            ? sortedProducts
            : sortedProducts.filter((p) => p.category === activeCategory);

    return (
        <section
            id="shop"
            data-testid="shop-section"
            className="relative bg-fgm-dark py-16 md:py-24 overflow-hidden"
        >
            {/* Summer Heat — animated ember + heat-haze backdrop (purely decorative) */}
            <div className="fgm-heat-overlay" aria-hidden="true">
                <div className="fgm-heat-glow" />
                <div className="fgm-heat-glow fgm-heat-glow--alt" />
                <div className="fgm-ember fgm-ember--1" />
                <div className="fgm-ember fgm-ember--2" />
                <div className="fgm-ember fgm-ember--3" />
                <div className="fgm-ember fgm-ember--4" />
                <div className="fgm-ember fgm-ember--5" />
                <div className="fgm-ember fgm-ember--6" />
                <div className="fgm-ember fgm-ember--7" />
                <div className="fgm-ember fgm-ember--8" />
            </div>
            <ProductSchema products={products} reviewsSummary={reviewsSummary} />
            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <span className="h-px w-12 bg-[#E63946]" />
                            <span className="font-display tracking-[0.35em] text-zinc-400 text-sm uppercase">
                                / 02 — The Drop
                            </span>
                        </div>
                        <h2
                            data-testid="shop-title"
                            className="font-display uppercase text-white text-5xl md:text-7xl leading-[0.9] tracking-tight"
                        >
                            <span className="font-outline-strong">Summer</span> Heat{" "}
                            <span className="block text-[#E63946] mt-2">Drop</span>
                            <span className="block text-[#E63946] text-3xl md:text-5xl mt-3 tracking-widest">
                                {isPreorderWindow() ? "Pre-Order Now" : "Now Available"}
                            </span>
                        </h2>
                    </div>
                    <p className="text-zinc-400 max-w-sm font-body">
                        {isPreorderWindow()
                            ? "Reserve yours before the drop. Pre-orders ship June 19. Lock your size now — quantities are limited."
                            : "The first FGM Apparel capsule is live. Grab yours before sizes sell out."}
                    </p>
                </div>

                {isPreorderWindow() && <DropCountdown />}

                {loading ? (
                    <div className="text-center py-20 text-zinc-500 font-display tracking-widest">
                        LOADING DROP…
                    </div>
                ) : (
                    <>
                        {categories.length > 2 && (
                            <div
                                data-testid="category-filter-bar"
                                className="flex flex-wrap items-center gap-2 mb-8 border-b border-white/10 pb-6"
                            >
                                {categories.map((cat) => {
                                    const isActive = activeCategory === cat;
                                    const count =
                                        cat === "ALL"
                                            ? products.length
                                            : products.filter((p) => p.category === cat).length;
                                    return (
                                        <button
                                            key={cat}
                                            data-testid={`category-filter-${cat.toLowerCase()}`}
                                            onClick={() => setActiveCategory(cat)}
                                            className={`group relative px-4 py-2 font-display tracking-[0.25em] text-xs uppercase transition-colors ${
                                                isActive
                                                    ? "text-white"
                                                    : "text-zinc-500 hover:text-zinc-200"
                                            }`}
                                        >
                                            {cat}
                                            <span className="ml-2 text-[10px] text-zinc-600">
                                                {count}
                                            </span>
                                            {isActive && (
                                                <span className="absolute left-0 right-0 -bottom-[25px] h-[2px] bg-[#E63946]" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <div
                            data-testid="product-grid"
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                        >
                            {visible.map((p) => (
                                <ProductCard
                                    key={p.id}
                                    product={p}
                                    onNotify={setNotifyFor}
                                    reviewSummary={reviewsSummary[p.id]}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <NotifyModal
                product={notifyFor}
                onClose={() => setNotifyFor(null)}
            />
        </section>
    );
}
